import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Lock, Key, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        reset_token: resetToken,
        new_password: newPassword
      });
      
      toast.success('Password reset successful! You can now login.');
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-coral-50 via-peach-50 to-teal-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Globe className="w-10 h-10 text-coral" />
            <h1 className="text-3xl font-bold">CultraMeet</h1>
          </div>
          <p className="text-gray-600">Create a new password</p>
        </div>

        <div className="glass-dark rounded-3xl p-8 shadow-2xl">
          <button
            onClick={() => navigate('/forgot-password')}
            className="flex items-center space-x-2 text-gray-600 hover:text-coral transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <h2 className="text-2xl font-bold mb-2">Reset Password</h2>
              <p className="text-gray-600 text-sm mb-4">
                Enter the reset token and your new password.
              </p>
            </div>

            <div>
              <Label htmlFor="reset-token" className="text-gray-700 font-medium">
                Reset Token
              </Label>
              <div className="relative mt-1">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="reset-token"
                  data-testid="reset-token-input"
                  type="text"
                  placeholder="Enter 6-digit token"
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value)}
                  className="pl-10 py-6 rounded-xl border-gray-200"
                  required
                  maxLength={6}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="new-password" className="text-gray-700 font-medium">
                New Password
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="new-password"
                  data-testid="new-password-input"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 py-6 rounded-xl border-gray-200"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="confirm-password" className="text-gray-700 font-medium">
                Confirm Password
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="confirm-password"
                  data-testid="confirm-password-input"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 py-6 rounded-xl border-gray-200"
                  required
                />
              </div>
            </div>

            <Button
              data-testid="reset-password-btn"
              type="submit"
              disabled={loading}
              className="w-full py-6 rounded-xl text-lg font-semibold bg-gradient-to-r from-coral to-peach hover:from-coral-dark hover:to-coral text-white"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
